import { api } from '../core';
import { BUNDLE_TAG } from '../constants';
import { type BundleTypeKeys, RootState, setBundleMetadata, setBundlePagesData } from '@/store';

export const BundleMetadataType = {
  LIGHT: 'LIGHT',
  FULL: 'FULL',
} as const;

export type BundleMetadataTypeKeys = (typeof BundleMetadataType)[keyof typeof BundleMetadataType];

interface PdfPageDimensions {
  height: number;
  width: number;
  /**
   * Page rotation in degrees, normalized to 0 | 90 | 180 | 270.
   * Optional: backend may omit it for un-rotated pages or older bundle
   * payloads, in which case consumers should treat it as 0.
   */
  pageRotation?: number;
  userPageRotation?: number;
}

export interface AnnotationLocation {
  bottomLeft: { x: number; y: number };
  bottomRight: { x: number; y: number };
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
}

export interface DateInfo {
  date: string;
  dateCategory: string;
  dateId: string;
  location: AnnotationLocation;
}

export interface PageLabel {
  labelId: string;
  labelValue: string;
}

export interface TextLabel {
  labelId: string;
  labelValue: string;
  location: AnnotationLocation;
}

interface BundleItemData {
  clusterDate: string;
  bookmarkDate: string | null;
  dates: DateInfo[];
  documentLength: number;
  documentName: string;
  highRelevance: boolean;
  inLightBundle: boolean;
  /** Optional: older bundle payloads may omit it, treat as not blank. */
  isBlank?: boolean;
  chronologyEntryIds: string[] | null;
  originalPageNumber: number;
  pageDimensions: PdfPageDimensions;
  pageLabels: PageLabel[];
  pageUri: string;
  textLabels: TextLabel[];
  thumbnailUri: string;
}

export interface BundleMetadata {
  bundleType: BundleMetadataTypeKeys | null;
  caseId: string | null;
  otherBundleId: string | null;
}

interface BundleResponse {
  metadata: BundleMetadata;
  pages: Record<string, BundleItemData>;
}

export interface BundleItemDataExtended extends BundleItemData {
  pageId: string;
}

type GetBundleProps = {
  bundleId: string;
  bundleKey?: BundleTypeKeys;
  abortController?: AbortController;
};

export const getBundleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getBundle: builder.query<BundleResponse, GetBundleProps>({
      query: ({ bundleId, abortController = new AbortController() }: GetBundleProps) => ({
        url: `bundle?bundle_id=${bundleId}`,
        method: 'GET',
        signal: abortController.signal,
      }),
      providesTags: (_result, _err, { bundleId }) => [{ type: BUNDLE_TAG, id: bundleId }],
      async onQueryStarted({ bundleKey }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const state = getState() as RootState;
          const resolvedBundleKey = bundleKey ?? state.sharedData.activeBundleType;

          if (data?.pages && data?.metadata && resolvedBundleKey) {
            dispatch(
              setBundlePagesData({
                key: resolvedBundleKey,
                data: new Map(
                  Object.entries(data.pages).map(([pageId, item]) => [
                    pageId,
                    {
                      ...item,
                      pageId,
                    },
                  ]),
                ),
              }),
            );

            dispatch(
              setBundleMetadata({
                key: resolvedBundleKey,
                data: data.metadata,
              }),
            );
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getBundle in onQueryStarted:', error);
          }
        }
      },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const { useLazyGetBundleQuery } = getBundleApi;
